/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {Utils, EEmbededElementTypes, RenderDocument} from "js_renderer";
import Element from "./element";
import {vec3} from "gl-matrix";
import Factory from "./factory";

export default
class BoundingBox extends Element {

    public static TypeName: string = EEmbededElementTypes.BOUNDINGBOX;
    private _positions: Float32Array;

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);

        this._positions = new Float32Array(8 * 3);
        this.extendProperties({
            color: [0.0, 0.0, 1.0],
            alwaysOnTop: false,
            pickable: false,
            notOutline: true
        });
    }

    public static create(aRenderDocument: RenderDocument): BoundingBox {
        let element = new BoundingBox(aRenderDocument);
        element._typeName = BoundingBox.TypeName;
        return element;
    }

    public static getPositionsForAllPlanes(aCenter: vec3, aDimensions: vec3): Float32Array {
        let positions = new Float32Array(8 * 3);

        // back points
        positions[0] = aCenter[0] - aDimensions[0] / 2.0; // left bottom back
        positions[1] = aCenter[1] - aDimensions[1] / 2.0;
        positions[2] = aCenter[2] - aDimensions[2] / 2.0;

        positions[3] = aCenter[0] + aDimensions[0] / 2.0; // right bottom back
        positions[4] = aCenter[1] - aDimensions[1] / 2.0;
        positions[5] = aCenter[2] - aDimensions[2] / 2.0;

        positions[6] = aCenter[0] + aDimensions[0] / 2.0; // right top back
        positions[7] = aCenter[1] + aDimensions[1] / 2.0;
        positions[8] = aCenter[2] - aDimensions[2] / 2.0;

        positions[9] = aCenter[0] - aDimensions[0] / 2.0; // left top back
        positions[10] = aCenter[1] + aDimensions[1] / 2.0;
        positions[11] = aCenter[2] - aDimensions[2] / 2.0;

        // front points
        positions[12] = aCenter[0] - aDimensions[0] / 2.0; // left bottom front
        positions[13] = aCenter[1] - aDimensions[1] / 2.0;
        positions[14] = aCenter[2] + aDimensions[2] / 2.0;

        positions[15] = aCenter[0] + aDimensions[0] / 2.0; // right bottom front
        positions[16] = aCenter[1] - aDimensions[1] / 2.0;
        positions[17] = aCenter[2] + aDimensions[2] / 2.0;

        positions[18] = aCenter[0] + aDimensions[0] / 2.0; // right top front
        positions[19] = aCenter[1] + aDimensions[1] / 2.0;
        positions[20] = aCenter[2] + aDimensions[2] / 2.0;

        positions[21] = aCenter[0] - aDimensions[0] / 2.0; // left top front
        positions[22] = aCenter[1] + aDimensions[1] / 2.0;
        positions[23] = aCenter[2] + aDimensions[2] / 2.0;

        return positions;
    }

    public static getIndicesForAllPlanes(): number[][] {
        return [
            [6, 5, 1, 2],
            [7, 4, 5, 6],
            [3, 0, 4, 7],
            [3, 0, 1, 2],
            [3, 7, 6, 2],
            [0, 4, 5, 1]
        ];
    }

    public static getNormalsForAllPlanes(): vec3[] {
        return [
            vec3.fromValues(1, 0, 0), // x
            vec3.fromValues(0, 0, -1), // -z
            vec3.fromValues(-1, 0, 0), // -x
            vec3.fromValues(0, 0, 1), // z
            vec3.fromValues(0, 1, 0), // y
            vec3.fromValues(0, -1, 0) // -y
        ];
    }

    public static getColorsForAllPlanes(): vec3[] {
        return [
            vec3.fromValues(1, 0, 0), // x
            vec3.fromValues(0, 0, 1), // -z
            vec3.fromValues(1, 0, 0), // -x
            vec3.fromValues(0, 0, 1), // z
            vec3.fromValues(0, 1, 0), // y
            vec3.fromValues(0, 1, 0) // -y
        ];
    }

    public static getPositionsForMiddlePlanes(aCenter: vec3, aDimensions: vec3): Float32Array {
        let positions = new Float32Array(12 * 3);
        let scale = 1.5;

        // XY plane (XZ for cadwork}
        positions[0] = aCenter[0] - aDimensions[0] / scale; // left bottom
        positions[1] = aCenter[1] - aDimensions[1] / scale;
        positions[2] = aCenter[2];

        positions[3] = aCenter[0] + aDimensions[0] / scale; // right bottom
        positions[4] = aCenter[1] - aDimensions[1] / scale;
        positions[5] = aCenter[2];

        positions[6] = aCenter[0] + aDimensions[0] / scale; //right top
        positions[7] = aCenter[1] + aDimensions[1] / scale;
        positions[8] = aCenter[2];

        positions[9] = aCenter[0] - aDimensions[0] / scale; // left top
        positions[10] = aCenter[1] + aDimensions[1] / scale;
        positions[11] = aCenter[2];

        // YZ (ZY for cadwork}
        positions[12] = aCenter[0]; // left bottom
        positions[13] = aCenter[1] - aDimensions[1] / scale;
        positions[14] = aCenter[2] + aDimensions[2] / scale;

        positions[15] = aCenter[0]; // right bottom
        positions[16] = aCenter[1] - aDimensions[1] / scale;
        positions[17] = aCenter[2] - aDimensions[2] / scale;

        positions[18] = aCenter[0]; // right top
        positions[19] = aCenter[1] + aDimensions[1] / scale;
        positions[20] = aCenter[2] - aDimensions[2] / scale;

        positions[21] = aCenter[0]; // left top
        positions[22] = aCenter[1] + aDimensions[1] / scale;
        positions[23] = aCenter[2] + aDimensions[2] / scale;

        // XZ (XY for cadwork)
        positions[24] = aCenter[0] - aDimensions[0] / scale; // left bottom
        positions[25] = aCenter[1];
        positions[26] = aCenter[2] + aDimensions[2] / scale;

        positions[27] = aCenter[0] + aDimensions[0] / scale; // right bottom
        positions[28] = aCenter[1];
        positions[29] = aCenter[2] + aDimensions[2] / scale;

        positions[30] = aCenter[0] + aDimensions[0] / scale; // right top
        positions[31] = aCenter[1];
        positions[32] = aCenter[2] - aDimensions[2] / scale;

        positions[33] = aCenter[0] - aDimensions[0] / scale; // left top
        positions[34] = aCenter[1];
        positions[35] = aCenter[2] - aDimensions[2] / scale;

        return positions;
    }

    public static getIndicesForMiddlePlanes(): number[][] {
        return [
            [0, 1, 2, 3],
            [4, 5, 6, 7],
            [8, 9, 10, 11]
        ];
    }

    public static getNormalsForMiddlePlanes(): vec3[] {
        return [
            vec3.fromValues(0, 0, -1),
            vec3.fromValues(1, 0, 0),
            vec3.fromValues(0, 1, 0)
        ];
    }

    public static getColorsForMiddlePlanes(): vec3[] {
        return [
            vec3.fromValues(0, 0, 1), // x
            vec3.fromValues(1, 0, 0), // y
            vec3.fromValues(0, 1, 0) // z
        ];
    }

    public setPositions(aCenter: vec3, aDimensions: vec3) {
        this._positions = BoundingBox.getPositionsForAllPlanes(aCenter, aDimensions);

        this.updatePositions([
            this._positions
        ]);
    }

    public getPositions(): Float32Array | number[] {
        if (this._positions.slice !== undefined) {
            return this._positions.slice();
        } else {
            let positions = [];
            for (let i = 0; i < this._positions.length; i++) {
                positions.push(this._positions[i]);
            }

            return positions;
        }
    }

    protected _generateElementDescriptionJSON(): any {
        return {
            "type": this.getTypeName(),
            "id": this.getTypeName() + "_" + this._UID,   // this is is not important in the engine, it can identify e.g. id of an element in Lexocad
            "UID": this._UID//,
        };
    }

    private _generateBinaryData(): ArrayBuffer {
        let binaryData = Utils.mergeBuffers(
            this._positions.buffer,
            new Uint32Array([
                0, 1,
                1, 2,
                2, 3,
                3, 0,
                4, 5,
                5, 6,
                6, 7,
                7, 4,
                0, 4,
                1, 5,
                2, 6,
                3, 7
            ]).buffer
        );

        return binaryData.buffer;
    }

    /***
     * We generate the base64 encoded binary data here as we would like to use the binaryGeometry class which accepts as input a response which is an array with data
     * @returns {{description: {id: string, type: string, bboxMin: *, bboxMax: *, color: Array}, appearance: {renderOption: {alwaysOnTop: boolean, pickable: boolean}}, binaryGeometry: {vertexCount: number, indexCount: number, offset: number, binSize: number, src: string}}}
     * @private
     */
    protected _generateGeometryObjectDescriptionJSON(): any {
        return [{
            description: {
                type: "OUTLINE",
                bboxMin: [this._positions[0], this._positions[1], this._positions[2]],
                bboxMax: [this._positions[18], this._positions[19], this._positions[20]],
                color: this._properties.color
            },
            appearance: {
                renderOption: {
                    alwaysOnTop: this._properties.alwaysOnTop,
                    pickable: this._properties.pickable,
                    clippable: this._properties.clippable,
                    notOutline: this._properties.notOutline
                }
            },
            binaryGeometry: {
                vertexCount: 8,
                indexCount: 24,
                offset: 0,
                binSize: 192,
                src: this._renderDocument.getRenderer()._dataStorage.createLocalResourceFromArrayBuffer(this._generateBinaryData())
            }
        }];
    }
}

Factory.register(BoundingBox);
